import type { FeatureEditSession } from '../../../edit/FeatureEditSession';
import { ExtrudeFeatureDefinitionModel } from '../../../definition/extrude/ExtrudeFeatureDefinition';
import { BossExtrudeFeaturePrototype } from '../../../prototype/extrude/BossExtrudeFeaturePrototype';
import { CutExtrudeFeaturePrototype } from '../../../prototype/extrude/CutExtrudeFeaturePrototype';
import type { ExtrudeFeaturePrototype } from '../../../prototype/extrude/ExtrudeFeaturePrototype';
import { resolveSketchProfileSourceSketchId } from '../../../../utils/feature/resolveSketchProfileSourceSketchId';
import { ExtrudeCommitExecution } from './ExtrudeCommitExecution';
import { FeatureCommitExecutionHandler, type FeatureCommitExecutionInput, type FeatureCommitExecutionResult } from '../FeatureCommitExecutionHandler';
import type { FeatureExecutionRuntime } from '../../FeatureExecutionRuntime';

/**
 * 拉伸特征提交执行处理器。
 */
export class ExtrudeCommitExecutionHandler extends FeatureCommitExecutionHandler {
    /** 拉伸提交执行器。 */
    private readonly execution: ExtrudeCommitExecution;
    /** 特征执行运行时。 */
    private readonly runtime: FeatureExecutionRuntime;

    /**
     * @param execution 拉伸提交执行器。
     */
    public constructor(execution: ExtrudeCommitExecution, runtime: FeatureExecutionRuntime) {
        super();
        this.execution = execution;
        this.runtime = runtime;
    }

    /**
     * @inheritdoc
     */
    public supports(input: FeatureCommitExecutionInput): boolean {
        return (
            (input.featureKind === 'boss-extrude' || input.featureKind === 'cut-extrude')
            && input.definition instanceof ExtrudeFeatureDefinitionModel
        );
    }

    /**
     * @inheritdoc
     * @throws 当提交输入中的定义不是拉伸特征定义时。
     */
    public async execute(input: FeatureCommitExecutionInput): Promise<FeatureCommitExecutionResult> {
        if (!(input.definition instanceof ExtrudeFeatureDefinitionModel)) {
            throw new Error('拉伸提交处理器收到的 definition 类型不正确');
        }
        const featureId = input.featureId ?? `feature:${Date.now()}`;
        const sourceSketchId = resolveSketchProfileSourceSketchId({
            definitionSourceSketchId: input.definition.getSourceSketchId(),
            bindings: input.bindings,
        });
        const prototype = input.featureKind === 'cut-extrude'
            ? new CutExtrudeFeaturePrototype({
                featureId,
                definition: input.definition,
                bindings: input.bindings,
                sourceSketchId,
            })
            : new BossExtrudeFeaturePrototype({
            featureId,
            definition: input.definition,
            bindings: input.bindings,
            sourceSketchId,
        });
        if (input.session) {
            const replaced = await this.execution.commitEdit(
                input.session as FeatureEditSession<ExtrudeFeatureDefinitionModel, ExtrudeFeaturePrototype>,
                prototype,
            );
            return {
                replaced,
                featureId,
                prototype,
                meshData: null,
            };
        }
        const objectIds = await this.execution.commitCreate(prototype);
        const meshDataList = await Promise.all(
            objectIds.map((objectId) => this.runtime.generateMeshData(objectId)),
        );
        return {
            replaced: false,
            featureId,
            prototype,
            meshData: meshDataList[0] ?? null,
            objectId: objectIds[0],
            objectIds,
            meshDataList,
        };
    }
}
